import {useState, useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {TState} from '../../../../redux/store.type';
import {getEventSettings} from '../../../../redux/event/eventActions';
import {getLeadWhatsappTemplate} from '../../../../redux/publish/publishActions';

interface LeadData {
    whatsapp: {
        image: {path: string, imageId: string};
        message: string;
    };
    email: {
        image: {path: string, imageId: string};
        emailBody: string;
        emailSubject: string;
    };
    sms: {
        message: string;
    };
}

export const useLeadData = () => {
    const {t} = useTranslation('mailing');
    const design = useSelector((s: TState) => s.event.settings.design);
    const getEventSettingsXHR = useSelector((s: TState) => s.event.getEventSettingsXHR);
    const whatsappTemplate = useSelector((s: TState) => s.publish.leadWhatsappTemplate);

    const getInitialImage = useCallback(() => {
        const mobileImage = {
            path: design?.fbImgMobile, 
            imageId: design?.fbImgMobileId, 
            credit: design?.credit?.mobile
        };
        const postImage = {
            path: design?.fbImgPost, 
            imageId: design?.fbImgPostId, 
            credit: design?.credit?.post
        };
        return mobileImage.path ? mobileImage : postImage.path ? postImage : {path: '', imageId: ''};
    }, [design]);

    const defaultMessage = t('mailingLeadDefaultText');
    const defaultMessageWithLink = `${t('leadMessageTitle')}\n${t('mailingLeadDefaultText')}\n [ref_link]`;

    const [whatsappData, setWhatsappData] = useState({
        image: {path: '', imageId: ''},
        message: defaultMessage
    });

    const [emailData, setEmailData] = useState({
        image: {path: '', imageId: ''},
        emailBody: defaultMessageWithLink,
        emailSubject: t('subjectDefaultText')
    });

    const [smsData, setSmsData] = useState({message: defaultMessageWithLink});

    const handleDataSave = useCallback((data: any, key: string, type: keyof LeadData) => {
        const setters = {
            whatsapp: setWhatsappData,
            email: setEmailData,
            sms: setSmsData
        };
        
        const setter = setters[type];
        if(setter) {
            setter(prev => ({...prev, [key]: data}));
        }
    }, []);

    // Initialize event settings
    useEffect(() => {
        if(!getEventSettingsXHR) {
            getEventSettings().then(); 
        }
        if(!whatsappTemplate) {
            getLeadWhatsappTemplate().then();
        }
    }, []);

    // Update image when design data becomes available
    useEffect(() => {
        if(design && (design.fbImgMobile || design.fbImgPost)) {
            const newImage = getInitialImage();
            if(newImage.path && newImage.path !== whatsappData.image.path) {
                setWhatsappData(prev => ({...prev, image: newImage}));
                setEmailData(prev => ({...prev, image: newImage}));
            }
        }
    }, [design]);

    return {
        whatsappData,
        emailData,
        smsData,
        handleDataSave,
        whatsappTemplate,
        variablesType: 'lead'
    };
};
